import React, { useState } from "react";
import { useStudio } from "@/studio/StudioContext";
import Fader from "@/studio/controls/Fader";
import Knob from "@/studio/controls/Knob";
import VUMeter from "@/studio/controls/VUMeter";
import InfoDot from "@/studio/InfoDot";

function ChannelStrip({ id }) {
  const { engine, selectedChannel, setSelectedChannel } = useStudio();
  const ch = engine.channels[id];
  const [, force] = useState(0);
  const u = () => force((n) => n + 1);

  const selected = selectedChannel === id;

  return (
    <div
      data-testid={`mixer-strip-${id}`}
      onClick={() => setSelectedChannel(id)}
      className="flex flex-col items-center gap-2 px-3 py-2 border-r border-[#262626] shrink-0 cursor-pointer transition-colors"
      style={{ width: 92, background: selected ? "#1c1c1c" : "transparent" }}
    >
      <div className="flex items-center gap-1 w-full justify-center">
        <span className="w-2 h-2 rounded-full" style={{ background: ch.color, boxShadow: `0 0 6px ${ch.color}` }} />
        <span className="hw-label text-[8px] truncate">{ch.name}</span>
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <Knob
          value={ch.state.pan}
          min={-1}
          max={1}
          size={34}
          color={ch.color}
          label="Pan"
          format={(v) => (v === 0 ? "C" : v < 0 ? `L${Math.round(-v * 100)}` : `R${Math.round(v * 100)}`)}
          hint="Pan — move the sound left or right in the stereo field"
          onChange={(v) => {
            ch.setPan(v);
            u();
          }}
          testId={`pan-${id}`}
        />
      </div>

      <div className="flex items-end gap-1.5 mt-1" onClick={(e) => e.stopPropagation()}>
        <Fader
          value={ch.state.volume}
          height={130}
          hint="Volume for this channel"
          onChange={(v) => {
            ch.setVolume(v);
            u();
          }}
          testId={`fader-${id}`}
        />
        <VUMeter channel={ch} height={130} />
      </div>

      <div className="flex gap-1 mt-1">
        <button
          data-testid={`mute-${id}`}
          title="Mute — silence this channel"
          onClick={(e) => {
            e.stopPropagation();
            ch.toggleMute();
            u();
          }}
          className="w-7 h-6 rounded-sm text-[9px] font-bold border transition-colors"
          style={
            ch.state.mute
              ? { background: "#ff3333", borderColor: "#ff3333", color: "#000" }
              : { borderColor: "#404040", color: "#a3a3a3" }
          }
        >
          M
        </button>
        <button
          data-testid={`solo-${id}`}
          title="Solo — hear only the soloed channels"
          onClick={(e) => {
            e.stopPropagation();
            ch.toggleSolo();
            u();
          }}
          className="w-7 h-6 rounded-sm text-[9px] font-bold border transition-colors"
          style={
            ch.state.solo
              ? { background: "#ffb800", borderColor: "#ffb800", color: "#000" }
              : { borderColor: "#404040", color: "#a3a3a3" }
          }
        >
          S
        </button>
      </div>
    </div>
  );
}

function MasterStrip() {
  const { engine, selectedChannel, setSelectedChannel } = useStudio();
  const [, force] = useState(0);
  const u = () => force((n) => n + 1);
  const ch = engine.started ? engine.masterChannel : null;
  const selected = selectedChannel === "master";
  const vol = ch ? ch.state.volume : 0.9;
  return (
    <div
      data-testid="mixer-strip-master"
      onClick={() => setSelectedChannel("master")}
      className="flex flex-col items-center gap-2 px-3 py-2 shrink-0 cursor-pointer border-l border-[#262626]"
      style={{ width: 92, background: selected ? "#1c1c1c" : "#111" }}
    >
      <span className="hw-label text-[8px] text-cyan-400">MASTER</span>
      <div style={{ height: 34 }} className="flex items-end">
        <span className="hw-label text-[7px] text-neutral-600">click to EQ</span>
      </div>
      <div className="flex items-end gap-1.5 mt-1" onClick={(e) => e.stopPropagation()}>
        <Fader
          value={vol}
          height={130}
          hint="Master output volume — the whole mix"
          onChange={(v) => {
            engine.ensureStarted();
            engine.setMasterVolume(v);
            u();
          }}
          testId="fader-master"
        />
        <VUMeter channel={ch || { getLevel: () => 0 }} height={130} />
      </div>
      <span className="font-mono-data text-[9px] text-cyan-400 mt-1">{Math.round(vol * 100)}</span>
    </div>
  );
}

export default function Mixer() {
  const { engine } = useStudio();
  const ids = Object.keys(engine.channels);
  return (
    <div className="flex flex-col h-full" data-testid="mixer">
      <div className="px-3 py-2 border-b border-[#262626]">
        <h3 className="font-display text-sm uppercase tracking-widest text-neutral-300 flex items-center gap-1.5">
          Mixing Console
          <InfoDot section="mixer" label="How the Mixing Console works" />
        </h3>
      </div>
      <div className="flex-1 flex overflow-x-auto">
        {ids.map((id) => (
          <ChannelStrip key={id} id={id} />
        ))}
        <MasterStrip />
      </div>
    </div>
  );
}
